import {
    Component,
    ElementRef,
    EventEmitter,
    Output,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { VideoStreamService } from 'app/global-services/video-stream.service';
import { from, Observable } from 'rxjs';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AuthService } from 'app/global-services/auth.service';

enum LEAD_CAPTURE_SCREEN {
    CAPTURE = 'capture',
    PARSING = 'parsing',
    SUCCESS = 'success',
    ERROR = 'error',
}

enum WEBCAM_FACING_MODE {
    FRONT = 'front',
    BACK = 'back',
}

@Component({
    selector: 'app-image-capture',
    templateUrl: './image-capture.component.html',
    styleUrls: ['./image-capture.component.scss'],
    standalone: false
})
export class ImageCaptureComponent {

    @Output() closeScreen = new EventEmitter();
    @ViewChild('videoElement', { static: false }) videoElement: ElementRef;
    @ViewChild('imageCaptureAudio', { static: false }) audioElement: ElementRef;
    @ViewChild('canvas', { static: false }) canvasElement: ElementRef;
    
    videoStream: MediaStream;
    
    isMobileDevice = true;
    isiOS = false;

    LEAD_CAPTURE_SCREEN = LEAD_CAPTURE_SCREEN;
    WEBCAM_FACING_MODE = WEBCAM_FACING_MODE;

    protected readonly navigator = navigator;
    
    capturedImage: File | null = null;
    tempCaptureImage: string | ArrayBuffer | null = '';

    currentLeadCaptureScreen: LEAD_CAPTURE_SCREEN = LEAD_CAPTURE_SCREEN.CAPTURE;
    webcamFacingMode: WEBCAM_FACING_MODE = WEBCAM_FACING_MODE.BACK;

    // State -> Loading, Camera Error, Flashlight
    loadingCamera: boolean = true;
    errorCamera: boolean = false;
    flashLight: boolean = false;

    // Set logged state
    isLoggedIn$: Observable<boolean>;

    constructor(
        private readonly videoStreamService: VideoStreamService,
        private readonly router: Router,
        private readonly deviceDetectorService: DeviceDetectorService,
        private readonly authService: AuthService
        
    ) {
        this.isLoggedIn$ = this.authService.isLoggedIn$;
    }

    // Check if the device is mobile or not and if it is iOS
    ngOnInit() {
        this.isMobileDevice = this.deviceDetectorService.isMobile();
        this.isiOS = this.deviceDetectorService.os === 'iOS';
    }

    ngAfterViewInit() {
        if (this.videoElement) {
            this.loadingCamera = true;
            this.isMobileDevice
                ? this.switchWebcamFacingMode(WEBCAM_FACING_MODE.BACK)
                : this.switchWebcamFacingMode(WEBCAM_FACING_MODE.FRONT);
        }
    }

    ngOnDestroy() {
        this.videoStreamService.stopWebcam();
    }

    handleCloseScreen() {
        this.router.navigate(['/cards'], {
            skipLocationChange: false,
        });
    }

    // Handle image click from webcam
    captureImage() {
        const video = this.videoElement.nativeElement;
        const canvas = this.canvasElement.nativeElement;

        // Set canvas size to match video dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert the canvas to an image data URL (PNG format)
        const imageDataUrl = canvas.toDataURL('image/png');
        const byteString = atob(imageDataUrl.split(',')[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
        }

        // Create a Blob from the ArrayBuffer
        const blob = new Blob([uint8Array], { type: 'image/jpeg' });

        // Create a File from the Blob
        const file = new File([blob], 'captured-image.jpeg', {
            type: 'image/jpeg',
        });
        this.capturedImage = file;
        this.tempCaptureImage = URL.createObjectURL(file);
        this.playAudio();
        this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.PARSING);
        this.showCaptureAnimation();
    }

    // Handle image upload from storage i.e on left of capture button
    handleUploadImageFromStorage(e) {
        const uploadedFile = e.target.files[0];
        this.capturedImage = uploadedFile;
        this.updateLeadImage(uploadedFile);
        this.showCaptureAnimation();
    }

    closeLeadListScreen() {
        this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.CAPTURE);
        this.loadingCamera = true;
        // Reset captured images
        setTimeout(() => {
            if (this.videoElement) {
                this.isMobileDevice
                    ? this.switchWebcamFacingMode(WEBCAM_FACING_MODE.BACK)
                    : this.switchWebcamFacingMode(WEBCAM_FACING_MODE.FRONT);
            }
        });
    }

    // Handling Flash light toogle state
    handleFlashToggle() {
        from(this.videoStreamService.toggleFlashlight(!this.flashLight)).subscribe({
            next: (resFlashStatus) => {
                this.flashLight = resFlashStatus;
            },
            error: (err) => {
                console.error('Error toggling flashlight:', err);
            }
        });
    }

    private switchLeadCaptureScreen(screen: LEAD_CAPTURE_SCREEN) {
        this.currentLeadCaptureScreen = screen;
    }

    private playAudio() {
        if (this.audioElement) {
            this.audioElement.nativeElement.play();
        } else {
            console.error('Audio element is not initialized yet!');
        }
    }

    private switchWebcamFacingMode(position: WEBCAM_FACING_MODE) {
        this.webcamFacingMode = position;
        this.initializeWebcam(position);
    }

    private handleWebcamError(err: any) {
        this.handleCameraError(err);
        console.error('Error accessing webcam:', err);
    }

    private handleCameraError(err: any) {
        console.error('Error initializing camera:', err);
        if (err.name === 'NotAllowedError') {
            this.errorCamera = true;
            console.error('Camera access denied. Please allow access in your browser settings and refresh.');
        }
        if (err.name === 'OverconstrainedError') {
            this.errorCamera = true;
            console.error('Requested camera type is not available. Using default camera.');
        }
    }

    // Store image in temp variable for preview
    private updateLeadImage(passedLeadImage: File) {
        const reader = new FileReader();
        reader.onload = () => {
            this.tempCaptureImage = reader.result;
        };
        reader.readAsDataURL(passedLeadImage);
    }

    private initializeWebcam(cameraType: WEBCAM_FACING_MODE = WEBCAM_FACING_MODE.BACK) {
        this.videoStreamService.startWebcam(cameraType)
            .then(() => {
                this.loadingCamera = false;
                this.errorCamera = false;
                this.videoStreamService.setVideoStream(this.videoElement);
            })
            .catch((err) => {
                this.loadingCamera = true;
                this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.ERROR);
                this.handleWebcamError(err);
            });
    }

    private showCaptureAnimation() {
        setTimeout(() => {
            this.videoStreamService.stopWebcam();
            this.switchLeadCaptureScreen(LEAD_CAPTURE_SCREEN.SUCCESS);
        }, 1000);
    }
}
